import { ViewStyle } from 'react-native';

// Regression guard for "Bug: Header overflow leads to unaligned stance indicator when screen too
// small": ContainerHeader's role/author/timestamp row wraps to a second line on a narrow screen.
// alignItems: 'center' (react-native-ui-lib's `centerV`) combined with flexWrap: 'wrap' aligns
// each line's content against the combined cross-axis size of *all* lines instead of just its
// own line, which pushes line-1 content (the stance indicator, rendered first) down out of
// alignment with the role label next to it as soon as a second line appears. 'flex-start' pins
// every line to its own top, so the indicator stays aligned whether the header wraps or not.
//
// Kept in its own dependency-free module (rather than inline in Containers.tsx) so the regression
// can be pinned down in a plain unit test: Containers.tsx transitively imports the Firebase native
// SDK via dataModel/firebaseWrapper, which jest can't load outside the app.
//
// `alignItems: 'flex-start'` pins content to the top of the row instead of vertically centering
// it (which is what made the misalignment bug possible in the first place), so a small
// paddingTop restores the breathing room above the content that centering used to provide for
// free.
export const CONTAINER_HEADER_WRAP_ROW_STYLE: ViewStyle = {
  flexWrap: 'wrap',
  flexDirection: 'row',
  alignItems: 'flex-start',
  paddingTop: 4,
};

// Regression guard for "thread connector and bend height are now on the same height, but above
// the text, make sure to center it vertically on the first line of text". Prior rounds (below)
// only ever validated the indicator and connector *against each other*, never against the text
// next to them — so once they matched each other, they could still both be wrong by the same
// amount, which is exactly what happened.
//
// Measured directly (Playwright against the real rendered app, Firebase emulators, a *registered,
// non-anonymous* account with a long display name so ContainerHeader's role/author/timestamp row
// wraps at a narrow/mobile viewport — an anonymous viewer gets every author's name anonymized,
// which hides the wrap entirely, see ContainerHeader's `anonymizeUser` prop):
//   - stance indicator pill: top=240, height=2 -> vertical center 241 (same as the previous
//     round's measurement — confirms the "now on the same height" part of the report)
//   - thread bend box (height 9, pre-this-round): top=233, bottom=242 -> border center 241
//   - role label text, the header's FIRST LINE of text ("Supporting Argument"): top=244,
//     bottom=260 -> vertical center 252
// That's an 11px gap between the matched indicator+connector pair (241) and the first line of
// text's own vertical center (252) — the pair sits 11px too high, exactly as reported. Adding
// this offset to both STANCE_INDICATOR_COMPENSATING_MARGIN_TOP and THREAD_CONNECTOR_BEND_HEIGHT
// (below) moves the pair down by 11px together (not the round-2 mistake of moving only the
// indicator), landing both on the text's center. Re-measured after the change: indicator center
// 252, connector border center 252, role label center 252 — all three aligned.
export const HEADER_FIRST_LINE_TEXT_CENTERING_OFFSET = 11;

// Regression guard for "Make sure that the line connecting the indicator with the thread line is
// on the same line" (review comment on the fix above). The row's own paddingTop (above) pushes
// every child in the row down by that same amount for breathing room, including the stance
// indicator (the row's first child) — without correction the indicator would drift paddingTop
// pixels away from where it sits relative to the role/user/timestamp text next to it.
//
// Subtracting the row's own paddingTop back out, as a per-item marginTop on just the stance
// indicator (not on the row itself), keeps the breathing room for the rest of the row's content
// while keeping the indicator's position relative to that text unchanged, in both the
// single-line and wrapped cases (the indicator is always the row's first child, so it is always
// on line 1). This is scoped to the indicator-vs-text relationship *within* ContainerHeader only.
//
// A later report ("the line is even higher now... text and round part seem to be aligned
// properly") confirmed the indicator-vs-text relationship this constant controls is correct, but
// that an *external* element — the thread/connector line drawn outside ContainerHeader
// (LineToSideOfViewDesktop / LineToSideOfViewMobile in StatementScreen.tsx, which bends into a
// horizontal arm at a fixed height to visually meet this indicator) — was not accounted for and
// got worse. Measuring both elements' actual rendered positions (Playwright against the Firebase
// emulators; see THREAD_CONNECTOR_BEND_HEIGHT below) confirmed that fixed height had never been
// updated for this row's current (flex-start + paddingTop) layout, and that trying to close that
// gap by changing *this* margin further would have re-broken the indicator-vs-text alignment that
// was correct at the time.
//
// A still later report ("thread connector and bend height are now on the same height, but above
// the text, make sure to center it vertically on the first line of text") confirmed the indicator
// and connector now match each other, but as a *pair* sit above the header's first line of text
// rather than centered on it — i.e. the target position itself (not just indicator-vs-connector)
// needed validating against the text, which no prior round had actually measured. See
// HEADER_FIRST_LINE_TEXT_CENTERING_OFFSET below for that measurement: this constant now also
// shifts the indicator down by that same offset, in addition to cancelling the row's paddingTop,
// so the indicator and the connector (THREAD_CONNECTOR_BEND_HEIGHT) move together and stay
// matched to each other while landing on the text's vertical center.
export const STANCE_INDICATOR_COMPENSATING_MARGIN_TOP: ViewStyle['marginTop'] =
  -(CONTAINER_HEADER_WRAP_ROW_STYLE.paddingTop as number) + HEADER_FIRST_LINE_TEXT_CENTERING_OFFSET;

// The external connector line (LineToSideOfViewDesktop / LineToSideOfViewMobile in
// StatementScreen.tsx) draws a short vertical "nub" (height 5, unchanged) immediately followed by
// this bend box, whose bottom border is the horizontal arm that should look like one continuous
// line with the stance indicator above (a 15x2 pill, see StanceIndicator in Containers.tsx).
//
// Measured directly (Playwright against the real rendered app, Firebase emulators, a seeded
// statement with two arguments, narrow/mobile viewport forcing the header to wrap — see the PR
// discussion for how prior rounds got this wrong from code-reading alone):
//   - stance indicator pill: top=240, height=2 -> vertical center 241 (page-relative, but only
//     the *difference* between the two elements matters since they render in the same row)
//   - thread bend box (old height 20): top=233, bottom=253 -> 2px-thick bottom border center 252
// That's an 11px gap between the indicator (241) and the connector's arm (252) — the connector
// sits 11px below where it needs to be to read as one continuous line with the indicator. Since
// the indicator-vs-text alignment is independently confirmed correct (see
// STANCE_INDICATOR_COMPENSATING_MARGIN_TOP above) and must not change, the fix lives entirely
// here: shortening the bend box by that same 11px (20 -> 9) moves its bottom border up by 11px
// without moving its top (which stays anchored right after the unchanged 5px nub), landing its
// center at 241 -- exactly the indicator's center. Re-measured after the change: bend box
// top=233, bottom=242 -> border center 241, matching the indicator exactly.
//
// A later round moved both this and the indicator back down by
// HEADER_FIRST_LINE_TEXT_CENTERING_OFFSET (see below) to land the matched pair on the header's
// first line of text instead of just on each other.
export const THREAD_CONNECTOR_BEND_HEIGHT = 9 + HEADER_FIRST_LINE_TEXT_CENTERING_OFFSET;
